import React, {
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { Box, Button, Drawer, Typography } from '@mui/material';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useFormWithSchema } from '../../../utils/functions/yupFormTypescript';
import {
  AutoCompleteController,
  InputController,
} from '../../../components';
import {
  CascadeField,
  useCropSeasonOptions,
} from '../hooks/useCropSeasonOptions';
import arrow from '../../../assets/icons/line_arrow-close-left.svg';

const TEAL = 'rgba(0,105,112,1)';

export type TProductForm = {
  productName: string;
  country: string;
  region: string;
  crop: string;
  hybridProductionCode?: string;
  technicalIngredient: string;
  brand: string;
  company: string;
  packageSize: string;
  unitOfMeasure: string;
  dosageValue: string;
  dosageBasis: string;
  advantaMaterialCode: string;
  uplMaterialCode?: string;
  uplProduct: string;
  active: string;
};

const defaultValues: TProductForm = {
  productName: '',
  country: '',
  region: '',
  crop: '',
  hybridProductionCode: '',
  technicalIngredient: '',
  brand: '',
  company: '',
  packageSize: '',
  unitOfMeasure: '',
  dosageValue: '',
  dosageBasis: '',
  advantaMaterialCode: '',
  uplMaterialCode: '',
  uplProduct: '',
  active: '',
};

const productSchema = yup.object({
  productName: yup
    .string()
    .required('Product Name is required'),
  country: yup.string(),
  region: yup.string().required('Region is required'),
  crop: yup.string().required('Crop is required'),
  hybridProductionCode: yup.string(),
  technicalIngredient: yup
    .string()
    .required('Technical Ingredient is required'),
  brand: yup.string().required('Brand is required'),
  company: yup.string().required('Company is required'),
  packageSize: yup
    .string()
    .required('Package Size is required')
    .matches(/^\d+(\.\d+)?$/, 'Enter a valid number'),
  unitOfMeasure: yup
    .string()
    .required('Unit of Measure is required'),
  dosageValue: yup
    .string()
    .required('Dosage Value is required')
    .matches(/^\d+(\.\d+)?$/, 'Enter a valid number'),
  dosageBasis: yup
    .string()
    .required('Dosage Basis is required'),
  advantaMaterialCode: yup
    .string()
    .required('Advanta Material Code is required'),
  uplMaterialCode: yup.string(),
  uplProduct: yup
    .string()
    .required('UPL Product is required'),
  active: yup.string().required('Active is required'),
});

const COUNTRY_OPTIONS = [
  { value: 'India', label: 'India' },
  { value: 'Thailand', label: 'Thailand' },
];

const UOM_OPTIONS = [
  { value: 'grams', label: 'grams' },
  { value: 'kg', label: 'kg' },
  { value: 'Millilitres', label: 'millilitres' },
  { value: 'Litres', label: 'litres' },
  { value: 'Each', label: 'each' },
];

const UPL_PRODUCT_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];


const DOSAGE_BASIS_OPTIONS = [
  { value: 'per unit area', label: 'per unit area' },
  { value: 'per plant', label: 'per plant' },
  { value: 'per litre of water', label: 'per litre of water' },
  { value: 'per kg of seed', label: 'per kg of seed' },
];

const ACTIVE_OPTIONS = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' },
];

const keepNumeric = (
  event: React.ChangeEvent<HTMLInputElement>
) => {
  const cleaned = event.target.value.replace(/[^\d.]/g, '');
  const [whole, ...decimals] = cleaned.split('.');
  event.target.value = decimals.length
    ? `${whole}.${decimals.join('')}`
    : whole;
};

type ProductDrawerProps = {
  open: boolean;
  onClose: () => void;
  mode?: 'add' | 'duplicate' | 'edit';
  initialData?: Partial<TProductForm> | null;
  onSave?: (
    data: TProductForm
  ) => Promise<boolean | void> | boolean | void;
  saving?: boolean;
};

export default function ProductDrawer({
  open,
  onClose,
  mode = 'add',
  initialData,
  onSave,
  saving = false,
}: ProductDrawerProps) {
  const countryName = useSelector(
    (state: RootState) => state.country.countryName
  );
  const lockedCountry =
    initialData?.country ||
    COUNTRY_OPTIONS.find(
      (opt) =>
        opt.value.toLowerCase() ===
        (countryName || '').toLowerCase()
    )?.value ||
    '';

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
    control,
    reset,
    watch,
    setValue,
  } = useFormWithSchema(productSchema, {
    mode: 'onChange',
    defaultValues,
  });

  const [watchRegion, watchCrop, watchHybrid] = watch([
    'region',
    'crop',
    'hybridProductionCode',
  ]);


  const clearField = useCallback(
    (field: CascadeField) => {
      const opts = { shouldValidate: true };
      if (field === 'region') setValue('region', '', opts);
      else if (field === 'crop') setValue('crop', '', opts);
      else if (field === 'hybrid')
        setValue('hybridProductionCode', '', opts);
    },
    [setValue]
  );


  const dosageBasisOptions = useMemo(() => {
    const own = initialData?.dosageBasis;
    return own &&
      !DOSAGE_BASIS_OPTIONS.some((o) => o.value === own)
      ? [...DOSAGE_BASIS_OPTIONS, { value: own, label: own }]
      : DOSAGE_BASIS_OPTIONS;
  }, [initialData]);

  const { regionOptions, cropOptions, hybridOptions } =
    useCropSeasonOptions({
      enabled: open,
      region: watchRegion,
      crop: watchCrop,
      hybrid: watchHybrid,
      clear: clearField,
    });

  useEffect(() => {
    if (open) {
      reset({
        ...defaultValues,
        ...((mode === 'duplicate' || mode === 'edit') &&
        initialData
          ? initialData
          : {}),
        country: lockedCountry,
      });
    }
  }, [open, mode, initialData, reset, lockedCountry]);

  const handleClose = () => {
    reset({ ...defaultValues, country: lockedCountry });
    onClose();
  };

  const submit =
    (handler?: ProductDrawerProps['onSave']) =>
    async (data: any) => {
      const result = await handler?.({
        ...(data as TProductForm),
        country: lockedCountry,
      });
      if (result !== false) handleClose();
    };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: '50vw',
          backgroundColor: '#fff',
        },
      }}>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #E0E0E0',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.04)',
        }}>
        <Typography
          fontSize={17}
          fontWeight={600}
          sx={{ lineHeight: 1 }}>
          {mode === 'edit'
            ? 'Edit Product'
            : mode === 'duplicate'
            ? 'Duplicate Product'
            : 'Add Product'}
        </Typography>

        <Box
          onClick={handleClose}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}>
          <img
            src={arrow}
            alt="close"
            style={{ width: 20, height: 20 }}
          />
        </Box>
      </Box>

  
      <Box
        component="form"
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          pt: 2,
          pb: 10,
        }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr',
            },
            columnGap: 2,
            rowGap: 0,
            '& .form-control_autocomplete.h-28': {
              height: 'auto',
            },
            '& .form-field-label': {
              marginTop: '6px',
            },
            '& .input-field input': {
              marginBottom: 0,
            },
            '& .form-field-error': {
              margin: 0,
              minHeight: '16px',
              lineHeight: '16px',
              fontSize: '11px',
            },
          }}>
          <InputController
            label="Product Name"
            placeholder="Enter name"
            register={register('productName')}
            error={errors?.productName?.message}
            required
          />
          <AutoCompleteController
            control={control}
            name="country"
            label="Country"
            placeholder="Select Country"
            options={COUNTRY_OPTIONS}
            required
            error={errors?.country?.message}
            disableClearable
            locked
          />
          <AutoCompleteController
            control={control}
            name="region"
            label="Region"
            placeholder="Select Region"
            options={regionOptions}
            required
            error={errors?.region?.message}
            disableClearable
          />
          <AutoCompleteController
            control={control}
            name="crop"
            label="Crop"
            placeholder="Select Crop"
            options={cropOptions}
            required
            error={errors?.crop?.message}
            disableClearable
          />
          <AutoCompleteController
            control={control}
            name="hybridProductionCode"
            label="Hybrid Production Code"
            placeholder="Select Code"
            options={hybridOptions}
            error={errors?.hybridProductionCode?.message}
            disableClearable
          />
          <InputController
            label="Technical Ingredient"
            placeholder="Enter ingredient"
            register={register('technicalIngredient')}
            error={errors?.technicalIngredient?.message}
            required
          />
          <InputController
            label="Brand"
            placeholder="Enter brand name"
            register={register('brand')}
            error={errors?.brand?.message}
            required
          />
          <InputController
            label="Company"
            placeholder="Enter ingredient"
            register={register('company')}
            error={errors?.company?.message}
            required
          />
          <InputController
            label="Package Size"
            placeholder="Enter value"
            register={register('packageSize')}
            changeHandler={keepNumeric}
            error={errors?.packageSize?.message}
            required
          />
          <AutoCompleteController
            control={control}
            name="unitOfMeasure"
            label="Unit of Measure"
            placeholder="Select unit type"
            options={UOM_OPTIONS}
            required
            error={errors?.unitOfMeasure?.message}
            disableClearable
          />
          <InputController
            label="Dosage Value"
            placeholder="Enter value"
            register={register('dosageValue')}
            changeHandler={keepNumeric}
            error={errors?.dosageValue?.message}
            required
          />
          <AutoCompleteController
            control={control}
            name="dosageBasis"
            label="Dosage Basis"
            placeholder="Select dosage basis"
            options={dosageBasisOptions}
            required
            error={errors?.dosageBasis?.message}
            disableClearable
          />
          <InputController
            label="Advanta Material Code"
            placeholder="Enter code"
            register={register('advantaMaterialCode')}
            error={errors?.advantaMaterialCode?.message}
            required
          />
          <InputController
            label="UPL Material Code"
            placeholder="Enter code"
            register={register('uplMaterialCode')}
            error={errors?.uplMaterialCode?.message}
          />
          <AutoCompleteController
            control={control}
            name="uplProduct"
            label="UPL Product"
            placeholder="Select value"
            options={UPL_PRODUCT_OPTIONS}
            required
            error={errors?.uplProduct?.message}
            disableClearable
          />
          <AutoCompleteController
            control={control}
            name="active"
            label="Active"
            placeholder="Select value"
            options={ACTIVE_OPTIONS}
            required
            error={errors?.active?.message}
            disableClearable
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
          px: 3,
          py: 2,
          borderTop: '1px solid #E0E0E0',
          backgroundColor: '#fff',
        }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            textTransform: 'none',
            color: TEAL,
            borderColor: '#d0d5dd',
            fontWeight: 500,
          }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || saving}
          onClick={handleSubmit(submit(onSave))}
          sx={{
            textTransform: 'none',
            backgroundColor: TEAL,
            color: '#fff',
            fontWeight: 500,
            '&:hover': {
              backgroundColor: 'rgba(0,105,112,0.9)',
            },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(0,105,112,0.4)',
              color: '#fff',
            },
          }}>
          Save
        </Button>
      </Box>
    </Drawer>
  );
}
